import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminGet, adminPost, adminPut, adminDelete, formatApiError } from "../lib/api";

export const GALLERY_CATEGORIES = [
  "Lumber",
  "Steel",
  "Framing",
  "Delivery",
  "Job Site",
  "General",
];

export const blankGalleryItem = () => ({
  id: null,
  url: "",
  label: "",
  category: "Lumber",
  order: 0,
});

/**
 * Encapsulates load/create/update/delete/reorder logic for the admin Gallery.
 * View components remain purely presentational.
 */
export const useGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminGet("/gallery"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (item, currentCount) => {
      if (!item.url.trim()) {
        toast.error("Image URL is required");
        return false;
      }
      try {
        if (item.id) {
          await adminPut(`/gallery/${item.id}`, item);
        } else {
          await adminPost("/gallery", { ...item, order: currentCount });
        }
        await load();
        toast.success("Saved");
        return true;
      } catch (e) {
        toast.error(formatApiError(e));
        return false;
      }
    },
    [load]
  );

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete this gallery image?")) return;
    try {
      await adminDelete(`/gallery/${id}`);
      setItems((s) => s.filter((g) => g.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const move = useCallback(
    async (id, dir) => {
      const idx = items.findIndex((g) => g.id === id);
      const swap = idx + dir;
      if (swap < 0 || swap >= items.length) return;
      const a = items[idx];
      const b = items[swap];
      try {
        await adminPut(`/gallery/${a.id}`, { ...a, order: b.order });
        await adminPut(`/gallery/${b.id}`, { ...b, order: a.order });
        await load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    },
    [items, load]
  );

  return { items, loading, save, remove, move };
};
